import React, { useCallback, useEffect, useState } from 'react';
import { Select, Divider, Input, Button, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';

/**
 * Natures officielles du courrier, et celles que le bureau a ajoutées.
 *
 * POURQUOI DEUX ORIGINES. Les natures officielles sont une énumération du
 * serveur, et plusieurs traitements filtrent dessus : états partenaires,
 * attentes de PV, classement automatique par fil de discussion. Les rendre
 * libres les ferait cesser de trouver ce qu'ils cherchent - et un filtre qui ne
 * trouve plus rien ne lève aucune erreur : le défaut passerait pour un mois
 * calme.
 *
 * Une nature ajoutée par le bureau s'enregistre donc comme `AUTRE`, et son
 * libellé propre voyage à part. Le métier gagne sa liste ouverte, le code garde
 * ses repères.
 *
 * La valeur manipulée est `{ nature, natureLibelle }` : un seul champ ne
 * suffirait pas à distinguer « AUTRE, sans plus » de « AUTRE, appelée Mise en
 * demeure ».
 */
export default function SelectNatureCourrier({
  naturesOfficielles,
  value,
  onChange,
  placeholder,
  style,
  allowClear = true,
}) {
  const { t } = useTranslation('commun');
  const [libres, setLibres] = useState([]);
  const [nouveau, setNouveau] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/v1/parametres?type=LISTE', { skipErrorToast: true });
      setLibres(
        (res.data?.data || [])
          .filter((p) => (p.cle || '').toUpperCase().startsWith('NATURE_COURRIER.'))
          .map((p) => p.valeur),
      );
    } catch {
      // Les natures officielles restent proposées : un incident de chargement
      // ne doit pas empêcher d'enregistrer un courrier.
      setLibres([]);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  /* La clé d'une nature libre la distingue d'une officielle du même nom : sans
     préfixe, choisir « Autre » l'aurait confondue avec l'énumération. */
  const cleLibre = (libelle) => `LIBRE::${libelle}`;

  const valeurSelect = value?.natureLibelle
    ? cleLibre(value.natureLibelle)
    : (value?.nature || undefined);

  const choisir = (v) => {
    if (!v) { onChange?.({ nature: null, natureLibelle: null }); return; }
    if (v.startsWith('LIBRE::')) {
      onChange?.({ nature: 'AUTRE', natureLibelle: v.slice('LIBRE::'.length) });
      return;
    }
    onChange?.({ nature: v, natureLibelle: null });
  };

  const deriverCode = (libelle) => (libelle || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  const ajouter = async () => {
    const libelle = nouveau.trim();
    if (!libelle) return;
    const code = deriverCode(libelle);
    if (!code) { toast.error(t('selectNatureCourrier.libelleInvalide')); return; }

    setEnregistrement(true);
    try {
      await axiosInstance.post('/v1/parametres', {
        typeParametre: 'LISTE',
        cle: `NATURE_COURRIER.${code}`,
        valeur: libelle,
        description: null,
      });
      await charger();
      choisir(cleLibre(libelle));
      setNouveau('');
      toast.success(t('selectNatureCourrier.natureAjoutee'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('selectNatureCourrier.ajoutImpossible'));
    } finally {
      setEnregistrement(false);
    }
  };

  const options = [
    ...naturesOfficielles.map((n) => ({ value: n.value, label: n.label })),
    ...libres.map((l) => ({ value: cleLibre(l), label: l })),
  ];

  return (
    <Select
      value={valeurSelect}
      onChange={choisir}
      placeholder={placeholder === undefined ? t('selectNatureCourrier.selectionner') : placeholder}
      style={style}
      allowClear={allowClear}
      showSearch
      optionFilterProp="label"
      options={options}
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '6px 0' }} />
          <Space.Compact style={{ width: '100%', padding: '0 6px 6px' }}>
            <Input
              size="small"
              value={nouveau}
              placeholder={t('selectNatureCourrier.ajouterNature')}
              onChange={(e) => setNouveau(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
            />
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              loading={enregistrement}
              onMouseDown={(e) => e.preventDefault()}
              onClick={ajouter}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            />
          </Space.Compact>
        </>
      )}
    />
  );
}
